import { Phone, MessageCircle, Instagram } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { SITE } from "../config/site";

const Face = ({ hair }) => (
    <>
        <circle cx="159" cy="106" r="7" fill="#F2C9A0" />
        <circle cx="241" cy="106" r="7" fill="#F2C9A0" />
        <circle cx="200" cy="104" r="42" fill="#F2C9A0" />
        {hair === "curly" ? (
            <path
                d="M157 98 C 152 58 178 50 200 50 C 226 50 248 62 243 98 C 238 80 230 76 224 86 C 220 70 208 68 201 78 C 194 64 180 68 180 82 C 170 76 160 84 157 98 Z"
                fill="#1F2937"
            />
        ) : (
            <path
                d="M155 100 C 150 40 250 40 245 100 C 240 82 232 78 230 90 C 224 74 212 72 208 84 C 200 70 188 74 186 88 C 178 78 168 84 166 96 C 161 90 157 94 155 100 Z"
                fill="#111827"
            />
        )}
        <path d="M177 91 q7 -4 14 -1" stroke="#1F2937" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        <path d="M209 90 q7 -3 14 1" stroke="#1F2937" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        <ellipse cx="184" cy="103" rx="6" ry="7.5" fill="#FFFFFF" />
        <ellipse cx="216" cy="103" rx="6" ry="7.5" fill="#FFFFFF" />
        <circle cx="185" cy="105" r="3" fill="#0F172A" />
        <circle cx="217" cy="105" r="3" fill="#0F172A" />
        <path d="M200 108 q-2 7 2 9" stroke="#D89F6F" strokeWidth="2" fill="none" strokeLinecap="round" />
        <path d="M188 126 q12 7 24 0" stroke="#0F172A" strokeWidth="3" fill="none" strokeLinecap="round" />
    </>
);

const StudentArt = ({ variant, symbol }) => (
    <svg viewBox="0 0 400 300" className="absolute inset-0 w-full h-full transition-transform duration-700 ease-out group-hover:scale-105" aria-hidden="true">
        <rect width="400" height="300" fill="#0B1220" />
        <ellipse cx="200" cy="130" rx="95" ry="100" fill="none" stroke="#38BDF8" strokeWidth="2" strokeDasharray="8 10" opacity="0.45" transform="rotate(-5 200 130)" />
        <text x="34" y="70" fill="#38BDF8" opacity="0.4" fontFamily="monospace" fontSize="44">{symbol}</text>
        <text x="330" y="240" fill="#64748B" opacity="0.45" fontFamily="monospace" fontSize="24">{variant === "book" ? "π" : "Δ"}</text>
        {variant === "book" ? (
            <>
                <Face hair="curly" />
                <rect x="188" y="140" width="24" height="18" rx="4" fill="#F2C9A0" />
                <path d="M200 154 C 140 154 106 188 100 300 L 300 300 C 294 188 260 154 200 154 Z" fill="#334155" />
                <path d="M180 156 L 200 180 L 220 156 L 211 151 L 200 166 L 189 151 Z" fill="#F8FAFC" />
                <path d="M200 180 L 200 300" stroke="#1E293B" strokeWidth="4" />
                <path d="M140 232 L 200 250 L 260 232 L 260 258 L 200 276 L 140 258 Z" fill="#F8FAFC" />
                <path d="M200 250 L 200 276" stroke="#94A3B8" strokeWidth="2" />
                <path d="M280 205 L 294 244" stroke="#38BDF8" strokeWidth="6" strokeLinecap="round" />
            </>
        ) : (
            <>
                <Face hair="straight" />
                <rect x="188" y="140" width="24" height="18" rx="4" fill="#F2C9A0" />
                <path d="M200 154 C 140 154 106 188 100 300 L 300 300 C 294 188 260 154 200 154 Z" fill="#334155" />
                <path d="M180 156 L 200 180 L 220 156 L 211 151 L 200 166 L 189 151 Z" fill="#F8FAFC" />
                <path d="M200 180 L 200 300" stroke="#1E293B" strokeWidth="4" />
                <path d="M138 218 L 262 218 L 272 264 L 128 264 Z" fill="#0F172A" stroke="#38BDF8" strokeWidth="2" />
                <text x="200" y="252" textAnchor="middle" fill="#38BDF8" fontFamily="monospace" fontSize="24">√x</text>
            </>
        )}
    </svg>
);

const Portrait = ({ tutor }) => (
    <a
        data-testid={`tutor-instagram-${tutor.id}`}
        href={tutor.instagram}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Profilo Instagram di ${tutor.name}`}
        className="relative block overflow-hidden aspect-[4/3] bg-[#0B1220]"
    >
        <StudentArt variant={tutor.id === "tommaso" ? "book" : "laptop"} symbol={tutor.symbol} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-transparent" />
        <span className="absolute top-4 left-4 font-mono text-xs uppercase tracking-[0.3em] text-sky-400 bg-[#020617]/80 px-3 py-1 border border-slate-800">
            {tutor.role}
        </span>
        <span className="absolute bottom-4 right-4 inline-flex items-center gap-2 font-mono text-xs text-slate-300 bg-[#020617]/80 px-3 py-2 border border-slate-700 group-hover:border-sky-400 group-hover:text-sky-400 transition-colors duration-300">
            <Instagram size={14} /> {tutor.instagramHandle}
        </span>
    </a>
);

const Tutors = () => (
    <section id="chi-siamo" className="relative py-32 sm:py-40">
        <div className="max-w-[1440px] mx-auto px-6 sm:px-10">
            <SectionHeading
                index="01"
                label="Chi siamo"
                title={
                    <>
                        Due persone, un telefono <br className="hidden sm:block" />
                        ciascuno. <em className="text-sky-400">Nient'altro in mezzo.</em>
                    </>
                }
            />
            <Reveal delay={0.15}>
                <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">
                    Niente call center, niente piattaforme: quando chiami rispondiamo noi.
                    Ci dividiamo gli studenti, ci conosciamo da anni e ce li passiamo quando
                    serve un taglio diverso.
                </p>
            </Reveal>

            <div className="mt-16 grid md:grid-cols-2 gap-8 lg:gap-12">
                {SITE.tutors.map((t, i) => (
                    <Reveal key={t.id} delay={0.1 + i * 0.15}>
                        <article
                            data-testid={`tutor-card-${t.id}`}
                            className="group border border-slate-800 bg-[#0F172A]/40 hover:border-sky-400/60 transition-colors duration-500"
                        >
                            <Portrait tutor={t} />
                            <div className="p-8">
                                <h3 className="font-serif text-3xl text-slate-50">{t.name}</h3>
                                <p className="mt-3 text-slate-400 leading-relaxed">{t.bio}</p>
                                <ul className="mt-5 space-y-2 border-t border-slate-800 pt-5" data-testid={`tutor-curriculum-${t.id}`}>
                                    {t.curriculum.map((c) => (
                                        <li key={c} className="flex items-start gap-3 font-mono text-xs text-slate-400">
                                            <span className="text-sky-400 mt-0.5">▸</span> {c}
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-6 font-mono text-xl text-slate-100 tracking-wide" data-testid={`tutor-phone-${t.id}`}>
                                    {t.phoneDisplay}
                                </p>
                                <div className="mt-6 flex flex-wrap gap-3">
                                    <a
                                        data-testid={`tutor-call-${t.id}`}
                                        href={t.phoneHref}
                                        className="inline-flex items-center gap-2 bg-slate-50 text-[#020617] font-semibold text-sm px-6 py-3 hover:bg-sky-400 transition-colors duration-300"
                                    >
                                        <Phone size={16} /> Chiama
                                    </a>
                                    <a
                                        data-testid={`tutor-whatsapp-${t.id}`}
                                        href={t.whatsapp}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 border border-slate-600 text-slate-100 font-semibold text-sm px-6 py-3 hover:border-sky-400 hover:text-sky-400 transition-colors duration-300"
                                    >
                                        <MessageCircle size={16} /> WhatsApp
                                    </a>
                                </div>
                            </div>
                        </article>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);
export default Tutors;
